import { container } from '../context/container';
import { MetricsService } from '../services/metrics.service';
import { MetricEventName } from '../types/metric-events';

/**
 * Emit a metric event from a module that must not take a DI edge on
 * `MetricsService`.
 *
 * Why not `@inject(MetricsService)`: xconvert-web's services are one import
 * graph away from a strongly-connected component, and a class referenced at
 * DECORATION time (`@inject(X)`) while its module is mid-evaluation is exactly
 * what TDZ-crashes the Next.js SSG build while leaving jest green
 * (see the 2026-06 refresh-token incident). Resolving the service at CALL time
 * from the binding-free `context/container` module adds no decoration-time
 * reference and no new edge into the service graph — the same shape
 * `services/session-bootstrap.ts` and `utils/pickup-transferred-files.ts` use.
 *
 * Two guarantees every caller relies on: it is a no-op during SSG/SSR (no
 * `window`), and it never throws. Observability is never load-bearing — an
 * upload must not fail because a counter did.
 */
export function emitMetric(
    event: MetricEventName,
    attributes: Record<string, string | number | boolean> = {},
): void {
    if (typeof window === 'undefined') return;
    try {
        container.get(MetricsService).emit(event, attributes);
    } catch { /* telemetry is never load-bearing */ }
}

/** The `MetricsService` singleton, or null when it cannot be resolved (SSR). */
export function metricsOrNull(): MetricsService | null {
    if (typeof window === 'undefined') return null;
    try {
        return container.get(MetricsService);
    } catch {
        return null;
    }
}
